import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Building2, Users, TrendingUp, ArrowLeft, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/stats`)
      ]);
      setCompanies(companiesRes.data);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.founder_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <header className="border-b border-[rgba(200,168,75,0.25)] bg-[#FAF7F0]/92 backdrop-blur-xl shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ArrowLeft className="h-6 w-6 text-[#C8A84B]" />
            <h1 className="text-2xl font-bold text-[#0D1B2A]">SKI AXIS</h1>
            <ArrowRight className="h-6 w-6 text-[#C8A84B]" />
          </div>
          <div className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate("/")} data-testid="home-link">
              Home
            </Button>
            <Button onClick={() => navigate("/apply")} className="bg-[#C8A84B] hover:bg-[#D9BC6A] text-[#07070A] font-bold shadow-lg hover:scale-105 transition-all duration-300" data-testid="new-application-btn">
              New Application
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="stat-companies">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Total Companies
                </CardDescription>
                <CardTitle className="text-3xl text-[#0D1B2A] text-[#C8A84B]">{stats.total_companies}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="stat-applications">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Applications
                </CardDescription>
                <CardTitle className="text-3xl text-[#0D1B2A] text-[#C8A84B]">{stats.total_applications}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="stat-pending">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Pending Review
                </CardDescription>
                <CardTitle className="text-3xl text-[#0D1B2A] text-[#C8A84B]">{stats.pending_applications || 0}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="stat-accepted">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Revenue
                </CardDescription>
                <CardTitle className="text-3xl text-[#0D1B2A] text-[#C8A84B]">${(stats.total_revenue || 0).toLocaleString()}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3A4A5C]" />
            <Input
              placeholder="Search companies or founders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        <Tabs defaultValue="companies" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="companies" data-testid="companies-tab">Companies</TabsTrigger>
            <TabsTrigger value="applications" data-testid="all-applications-tab">All Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="space-y-4">
            {filteredCompanies.length === 0 ? (
              <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="no-companies-card">
                <CardContent className="pt-6">
                  <p className="text-center text-[#3A4A5C]">No companies found.</p>
                </CardContent>
              </Card>
            ) : (
              filteredCompanies.map((company) => (
                <Card
                  key={company.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer border-[rgba(200,168,75,0.2)]"
                  onClick={() => navigate(`/company/${company.id}`)}
                  data-testid={`company-card-${company.id}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{company.name}</CardTitle>
                        <CardDescription className="mt-2">
                          <div className="space-y-1">
                            <p><strong>Founder:</strong> {company.founder_name}</p>
                            <p><strong>Email:</strong> {company.founder_email}</p>
                            {company.website && (
                              <p>
                                <strong>Website:</strong>{" "}
                                <a
                                  href={company.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#C8A84B] hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {company.website}
                                </a>
                              </p>
                            )}
                          </div>
                        </CardDescription>
                      </div>
                      <Badge
                        className={company.payment_status === "paid" ? "bg-green-600" : "bg-yellow-600"}
                        data-testid={`company-status-${company.id}`}
                      >
                        {company.payment_status || "pending"}
                      </Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="applications" className="space-y-4">
            {applications.length === 0 ? (
              <Card className="border border-[rgba(13,27,42,0.08)] bg-white" data-testid="no-apps-card">
                <CardContent className="pt-6">
                  <p className="text-center text-[#3A4A5C]">No applications yet.</p>
                </CardContent>
              </Card>
            ) : (
              applications.map((app) => {
                const company = companies.find(c => c.id === app.company_id);
                return (
                  <Card
                    key={app.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer border-[rgba(200,168,75,0.2)]"
                    onClick={() => company && navigate(`/company/${company.id}`)}
                    data-testid={`application-card-${app.id}`}
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <CardTitle>{app.idea_description}</CardTitle>
                          </div>
                          {company && (
                            <p className="text-sm text-[#3A4A5C] mb-2">
                              <strong>{company.name}</strong> by {company.founder_name}
                            </p>
                          )}
                          <CardDescription>
                            <p className="line-clamp-2">{app.problem_statement}</p>
                          </CardDescription>
                        </div>
                        <Badge
                          className={app.status === "evaluated" ? "bg-green-600" : "bg-yellow-600"}
                          data-testid={`app-status-${app.id}`}
                        >
                          {app.status}
                        </Badge>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;